import React, { useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Filter as FilterIcon } from '@mint-lib/icons';
import { Button, Checkbox, TruncateText } from '@mint-lib/tw-components';
import {
  ComboboxInput,
  ComboboxInputLabel,
  ComboboxInputRoot,
  ComboboxItem,
  ComboboxMenu,
  ProgressCircular,
} from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';

export type SelectOption = {
  value: string;
  label: string;
};

type Props = {
  label?: string;
  placeholder?: string;
  inputValue?: string | null;
  selectedValues: string[];
  options: SelectOption[];
  onChange: (values: string[]) => void;
  isLoading?: boolean;
  withSearch?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium';
  noOptionsText?: string;
  className?: string;
  onOpenChange?: (open: boolean) => void;
};

export const MultiSelect = ({
  label,
  placeholder,
  inputValue,
  selectedValues,
  options,
  onChange,
  isLoading,
  withSearch = true,
  disabled,
  size,
  noOptionsText,
  className,
  onOpenChange,
}: Props) => {
  const { t } = useTranslation('@mint-lib/recommendations-circuit');

  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const isAllSelected = selectedValues.length === options.length;

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearchValue('');
    }

    if (onOpenChange) {
      onOpenChange(open);
    }
  };

  const handleSelect = (value: string) => {
    const option = options.find((opt) => opt.value === value);
    if (!option) return;

    const isSelected = selectedValues.includes(value);

    if (isSelected) {
      onChange(selectedValues.filter((val) => val !== value));
    } else {
      onChange([...selectedValues, option.value]);
    }
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      onChange([]);
    } else {
      onChange(options.map(({ value }) => value));
    }
  };

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchValue.toLowerCase()),
  );

  const defaultInputValues = selectedValues.length
    ? options
        .filter(({ value }) => selectedValues.includes(value))
        .map(({ label }) => label)
        .join(', ')
    : '';

  const isVisibleSelectAllBtn =
    searchValue.length > 0 ? filteredOptions.length > 0 : options.length > 0;

  return (
    <ComboboxInputRoot className={className}>
      {label && <ComboboxInputLabel label={label} htmlFor={label} />}
      <ComboboxInput
        placeholder={placeholder}
        value={inputValue || defaultInputValues}
        open={isOpen}
        onOpenChange={handleOpenChange}
        disabled={disabled}
        size={size}
        startIcon={<FilterIcon />}
        popoverClassName="z-popover"
      >
        <ComboboxMenu
          shouldFilter={false}
          withSearch={withSearch}
          onValueChange={setSearchValue}
          emptyMessage={
            isLoading ? (
              <ProgressCircular
                size="small"
                data-testid="select-progress-loader"
              />
            ) : (
              (noOptionsText ?? t('No options'))
            )
          }
        >
          <div
            className={cn(
              'max-h-[300px] scroll-py-1 overflow-x-hidden overflow-y-auto',
            )}
          >
            {isVisibleSelectAllBtn && (
              <Button
                onClick={handleSelectAll}
                variant="plain"
                className="mt-1 p-3"
              >
                {isAllSelected ? 'Clear all' : t('Select all')}
              </Button>
            )}
            {filteredOptions.map((option) => {
              const isSelected = selectedValues.includes(option.value);
              return (
                <ComboboxItem
                  key={option.value}
                  value={option.value}
                  onSelect={handleSelect}
                >
                  <div className="flex min-w-0 items-center gap-1">
                    <Checkbox checked={isSelected} />
                    <TruncateText>
                      <div>{option.label}</div>
                    </TruncateText>
                  </div>
                </ComboboxItem>
              );
            })}
          </div>
        </ComboboxMenu>
      </ComboboxInput>
    </ComboboxInputRoot>
  );
};
